/* eslint-disable @nx/workspace/a11y/require-focus-lock */
/* eslint-disable @nx/workspace/a11y/forbid-missing-initial-focus */
/* eslint-disable @nx/workspace/a11y/require-dialog-role */
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { Icon } from '@osmosis/tailwind-ui/components/Icon';
import {
  IconButton,
  IconButtonProps,
} from '@osmosis/tailwind-ui/components/IconButton';
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF';
import { faCode } from '@fortawesome/pro-duotone-svg-icons/faCode';
import { faClock } from '@fortawesome/pro-regular-svg-icons/faClock';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faShareNodes } from '@fortawesome/pro-regular-svg-icons/faShareNodes';

import React, { useState } from 'react';
import useModal from '../hooks/useModal';
import PopupMenu, { PopupMenuItem } from './PopupMenu';
import PopupMenuRow from './PopupMenuRow';
import PopupMenuRowWithToggle from './PopupMenuRowWithToggle';
import Snackbar from './Snackbar';
import EmbedVideoModal from './learn/EmbedVideoModal';
import CopyLinkSnackBar from '@osmosis/nextjs-components/Snackbar/CopyLinkSnackBar';
import { useIntl } from 'react-intl';
import { useVideoPlayer } from '../context/VideoPlayerProvider';
import { shareOnFacebook } from '../lib/frontend/share';
import { useLearnData } from '../context/LearnDataProvider';
import { useUserContext } from '@osmosis/nextjs-components/context';
import { useCreateDeepLink } from '@osmosis/nextjs-components/hooks/useCreateDeepLink';

type Props = {
  url?: string;
  thumbnail?: string;
  iconButtonProps?: IconButtonProps;
  concept: string;
  /**
   * When provided, the trigger renders as a labeled (icon + text) button
   * instead of the default icon-only button. Used by the v2 Learn header.
   */
  label?: string;
};

/**
 * Share button with dropdown options.
 */
const LearnShareButton = (props: Props) => {
  const { thumbnail = '', iconButtonProps = {}, concept, label } = props;

  const { isVisible, hide, toggle } = useModal();
  const embedModal = useModal();
  const intl = useIntl();
  const { progressState } = useVideoPlayer();
  const { isEmbedRemovalActive, learnData } = useLearnData();
  const currentPosition = progressState.playedSeconds;

  const [useTimestamp, setUseTimestamp] = useState(false);
  const [showCopySnackBar, setShowCopySnackBar] = useState(false);
  const { user, hasEolsToken } = useUserContext();
  const hasAdminAccess = Object.keys(user?.admin || {}).length > 0;
  const { createDeepLink } = useCreateDeepLink();

  const isVideo = !!thumbnail;

  const handleCopyLink = () => {
    const newUrl = new URL(window.location.href);

    if (useTimestamp && currentPosition) {
      newUrl.searchParams.set('t', Math.round(currentPosition).toString());
    }

    if (navigator?.clipboard) {
      navigator.clipboard.writeText(newUrl.href).then(() => {
        setShowCopySnackBar(true);
        hide();
      });
    }
  };

  const handleCreateDeepLink = async () => {
    await createDeepLink({
      url: window.location.href,
      learnNodeID: learnData?.learnNodeID,
    });
  };

  const handleShowEmbedModal = () => {
    embedModal.show();
    hide();
  };

  const handleToggle = () => {
    setUseTimestamp(!useTimestamp);
  };

  const shareAriaLabel = intl.formatMessage({
    defaultMessage: 'Share',
    id: 'share-button-share',
  });

  const {
    iconSize,
    ariaLabel: callerAriaLabel,
    ...restIconButtonProps
  } = iconButtonProps;

  return (
    <Box>
      <PopupMenu
        keyExtractor={(item) => item.id}
        isVisible={isVisible}
        hide={hide}
        renderItem={(item) =>
          item?.toggler ? (
            <PopupMenuRowWithToggle
              item={item}
              checked={useTimestamp}
              onToggle={handleToggle}
              testDataKey={'share-learn'}
              className="!text-gray-800"
            />
          ) : (
            <PopupMenuRow
              dataTestid={`popup-menu-item-${item.id}`}
              item={item}
              className="!text-gray-800"
            />
          )
        }
        heading={intl.formatMessage({
          defaultMessage: 'Share',
          id: 'share-button-share',
        })}
        centerHeading={true}
        items={
          [
            !!isVideo && {
              id: 'current_timestamp',
              text: intl.formatMessage({
                defaultMessage: 'Include timestamp',
                id: 'share-button-current-timestamp',
              }),
              icon: faClock,
              toggler: true,
            },
            {
              id: 'copy_link',
              text: intl.formatMessage({
                defaultMessage: 'Copy link',
                id: 'share-button-copy-link',
              }),
              icon: faLink,
              onClick: handleCopyLink,
            },
            (user?.isFaculty || hasAdminAccess) &&
              hasEolsToken && {
                id: 'create_deep_link',
                text: intl.formatMessage({
                  defaultMessage: 'Create deep-link',
                  id: 'share-button-create-deep-link',
                }),
                icon: faLink,
                onClick: handleCreateDeepLink,
              },
            !isEmbedRemovalActive && {
              id: 'facebook',
              text: 'Facebook',
              icon: faFacebookF,
              onClick: shareOnFacebook,
            },
            !!isVideo &&
              !isEmbedRemovalActive && {
                id: 'embed',
                text: intl.formatMessage({
                  defaultMessage: 'Embed',
                  id: 'share-button-embed',
                }),
                icon: faCode,
                onClick: handleShowEmbedModal,
              },
          ].filter(Boolean) as (PopupMenuItem & { toggler?: boolean })[]
        }
        containerClasses="min-w-[300px]"
      >
        {label ? (
          <Button
            data-testid="sharebutton-toggle"
            variant="transparent"
            color="default"
            startIcon={<Icon icon={faShareNodes} size={iconSize} />}
            onClick={toggle}
            aria-label={callerAriaLabel ?? shareAriaLabel}
            className={restIconButtonProps.className}
          >
            {label}
          </Button>
        ) : (
          <IconButton
            data-testid="sharebutton-toggle"
            variant="outline"
            icon={<Icon icon={faShareNodes} size={iconSize} />}
            onClick={toggle}
            {...restIconButtonProps}
            ariaLabel={callerAriaLabel ?? shareAriaLabel}
          />
        )}
      </PopupMenu>
      {/* eslint-disable-next-line @nx/workspace/a11y/require-aria-live-for-toast */}
      <Snackbar
        isVisible={showCopySnackBar}
        hide={() => setShowCopySnackBar(false)}
        message={<CopyLinkSnackBar />}
        variant="positive"
      />

      <EmbedVideoModal
        isVisible={embedModal.isVisible}
        hide={embedModal.hide}
        thumbnail={thumbnail}
        concept={concept}
      />
    </Box>
  );
};

export default LearnShareButton;
